import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #closeRequestId = 0;
  #featuredSliderUpdateTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onFeaturedSliderArrowClick);
    this.addEventListener('scroll', this.#onFeaturedSliderScroll, true);
    window.addEventListener('resize', this.#updateFeaturedContentControls);
    this.#setupAnimatedElementListeners();
    this.#updateFeaturedContentControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onFeaturedSliderArrowClick);
    this.removeEventListener('scroll', this.#onFeaturedSliderScroll, true);
    window.removeEventListener('resize', this.#updateFeaturedContentControls);
    clearTimeout(this.#featuredSliderUpdateTimeout);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    const details = this.#getDetailsElement(event);
    const isOpen = details.hasAttribute('open') || details.classList.contains('menu-open');

    return isOpen ? this.#close(details) : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#closeRequestId += 1;
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
        this.#scheduleFeaturedContentControlUpdate();
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const closeRequestId = ++this.#closeRequestId;
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      if (closeRequestId !== this.#closeRequestId) return;

      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Handle arrow button clicks for featured content sliders.
   * @param {MouseEvent} event
   */
  #onFeaturedSliderArrowClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-featured-content-arrow]');
    if (!(button instanceof HTMLButtonElement)) return;

    const listId = button.getAttribute('aria-controls');
    if (!listId) return;

    const list = this.querySelector(`#${listId}`);
    if (!(list instanceof HTMLElement)) return;

    const direction = button.dataset.featuredContentArrow === 'next' ? 1 : -1;
    const scrollAmount = Math.max(list.clientWidth * 0.9, 1);
    list.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });

    this.#scheduleFeaturedContentControlUpdate();
  };

  /**
   * Keep slider arrows in sync with scroll position.
   * @param {Event} event
   */
  #onFeaturedSliderScroll = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.classList.contains('menu-drawer__featured-content-list')) return;

    this.#updateFeaturedControlsForList(target);
  };

  #scheduleFeaturedContentControlUpdate() {
    clearTimeout(this.#featuredSliderUpdateTimeout);
    this.#featuredSliderUpdateTimeout = setTimeout(() => {
      this.#updateFeaturedContentControls();
    }, 220);
  }

  #updateFeaturedContentControls = () => {
    const featuredLists = this.querySelectorAll('.menu-drawer__featured-content-list');

    for (const list of featuredLists) {
      if (!(list instanceof HTMLElement)) continue;
      this.#updateFeaturedControlsForList(list);
    }
  };

  /**
   * @param {HTMLElement} list
   */
  #updateFeaturedControlsForList(list) {
    const listId = list.getAttribute('id');
    if (!listId) return;

    const controls = this.querySelectorAll(`[data-featured-content-arrow][aria-controls="${listId}"]`);
    if (!controls.length) return;

    const maxScrollLeft = Math.max(list.scrollWidth - list.clientWidth, 0);
    const isScrollable = maxScrollLeft > 1;
    const atStart = list.scrollLeft <= 1;
    const atEnd = list.scrollLeft >= maxScrollLeft - 1;

    for (const control of controls) {
      if (!(control instanceof HTMLButtonElement)) continue;

      const isPreviousButton = control.dataset.featuredContentArrow === 'prev';
      control.hidden = !isScrollable;
      control.disabled = isPreviousButton ? atStart : atEnd;
    }
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
